import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action, Dispatch } from 'redux';

import { CallStatus } from '../../lib/enums';
import { RootState } from '../../lib/store';
import { getAutoPass } from '../../lib/store/crm';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import { PaymentCall } from '../../lib/store/payment/types';
import { getAccountTradeHistory, getOpenPositions } from '../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData } from '../../lib/store/reporting/types';
import { isUserVerifiedStatus } from '../../lib/store/selectors';
import { getBuyingPower } from '../../lib/store/trading';
import { isDemoAccountActivating } from '../../lib/util/account-helpers';
import { useMarketTimer } from '../../lib/util/hooks/MarketTimerHook';
import { isEmpty } from '../../lib/util/typeGuards';
import { isPlanPurchaseCallbackInProgress } from '../../lib/util/url-helpers';

import { Grid } from '../../components';
import OrderResponseManager from '../../components/OrderResponseManager/OrderResponseManager';
import { useElementVisibilityById, useIsMobile } from '../../hooks';

import LossReachedContainer from './components/LossReachedContainer/LossReachedContainer';
import AccountBreakDown from './TableContent/MyAccountTableContent';
import { AdditionalInfo, PlanContainer, PnlContainer, ScoreCalculator } from './components';

import styles from './Dashboard.module.scss';

type Props = ReduxProps;

const Dashboard = (props: Props) => {
  const {
    accountReferenceId,
    hasPendingTradingAccount,
    individualData,
    hasDeposit,
    isUserVerified,
    isPaymentsReady,
    transactions,
    getTradeHistory,
    getPositions,
    fetchBuyingPowerAndPutGlobalState,
    getAutoPassStatus,
    accountStatusData,
  } = props;

  useMarketTimer('create-order');

  // An account which is still being created answers 503 on all of the calls below, so they wait for
  // the `AccountStatusChanged` event to bring in a usable account.
  const isAccountBeingCreated = hasPendingTradingAccount
    || isDemoAccountActivating()
    || isPlanPurchaseCallbackInProgress();

  const { isTablet, isMobile } = useIsMobile();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isUserReadyToTrade, setIsUserReadyToTrade] = useState<boolean>(true);
  const { dailyLossReached, dailyResetEvent, totalLossReached } = accountStatusData;
  const isRewindTradingDayNotification = useElementVisibilityById('rewindTradingDayNotification');
  const isCompleted = dailyResetEvent?.isCompleted;

  useEffect(() => {
    if (accountReferenceId && !isAccountBeingCreated) {
      getPositions();
      getTradeHistory({
        tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
        rowsPerPage: 30,
        isPendingOrdersCall: true,
      });
      setIsInitialized(true);
    }
  }, [accountReferenceId, isAccountBeingCreated, getPositions, getTradeHistory]);

  useEffect(() => {
    if (!isAccountBeingCreated) {
      getAutoPassStatus();
    }
  }, [isAccountBeingCreated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((!isUserVerified || isPaymentsReady) && !hasDeposit && isEmpty(transactions) && individualData) {
      setIsUserReadyToTrade(false);
    }
  }, [isUserVerified, isPaymentsReady, transactions, hasDeposit, individualData]);

  useEffect(() => {
    if (accountReferenceId && !isAccountBeingCreated) {
      fetchBuyingPowerAndPutGlobalState();
    }
  }, [accountReferenceId, isAccountBeingCreated]);// eslint-disable-line react-hooks/exhaustive-deps

  const renderExtraChildren = (): JSX.Element => (
    <>
      <PlanContainer />
      <ScoreCalculator />
      <AdditionalInfo />
      <OrderResponseManager />
    </>
  );

  return (
    <Grid
      extraChildren={!(isTablet || isMobile) ? renderExtraChildren() : null}
    >
      <div className={styles.wrapper}>
        {(isTablet || isMobile) && renderExtraChildren()}
        {dailyLossReached && !isRewindTradingDayNotification && !isCompleted && !totalLossReached
          ? <LossReachedContainer /> : (
            <>
              <PnlContainer />
              <AccountBreakDown isMyAccountInitiliazed={isInitialized} isUserVerified={isUserReadyToTrade} />
            </>
          )}
      </div>
    </Grid>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountReferenceId: getCurrentAccount(state),
  hasPendingTradingAccount: !!state.crm.pendingTradingAccountReference,
  individualData: state.crm.individualExtendedInfo,
  hasDeposit: state.payment.hasDeposit,
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  isPaymentsReady: state.payment.statusByCall[PaymentCall.getPaymentsAll]?.status === CallStatus.READY,
  transactions: state.payment.paymentsAll.data,
  accountStatusData: state.closeout.accountStatusData,
});
const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getTradeHistory: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getPositions: () => dispatch(getOpenPositions()),
  fetchBuyingPowerAndPutGlobalState: () => dispatch(getBuyingPower()),
  getAutoPassStatus: () => dispatch(getAutoPass()),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Dashboard);
